import React, { useState, useEffect } from 'react'
import clsx from 'clsx'
import { ReactComponent as IconUser } from 'images/user.svg';
import { ReactComponent as IconCheck } from 'images/check2.svg';
import { ReactComponent as IconNoCheck } from 'images/no-check2.svg';
import MessageModal from 'components/shared/modals/MessageModal';
import ConfirmView from 'modules/auth/components/ConfirmView';
import AuthModal from 'components/shared/modals/AuthModal';
import Button from 'components/shared/Button';
import useLogin from 'sdk/hooks/useLogin';
import useResetPassword from 'sdk/hooks/user/useResetPassword';
import { getMaskedEmail, getMasketPhone } from 'sdk/utils/maskedValues';
import { Accounts } from 'sdk/hooks/user/useUserAccounts';
import { useNavigate } from 'react-router-dom';
import styles from './SelectView.module.scss';

type SelectViewProps = {
  handleNext: () => void,
  accounts: Accounts,
  onClose: () => void,
}

type CheckBtnProps = {
  isActive?: boolean,
  isOnly?: boolean,
  title?: string,
  value?: string,
  onClick?: () => void,
}

enum Modes { Email, Phone }

function CheckBtn({
  isActive,
  isOnly,
  title = '',
  value = '',
  onClick = () => {},
}: CheckBtnProps) {
  if (!value) return null

  const Icon = isActive ? IconCheck : IconNoCheck

  return (
    <div
      className={clsx(
        styles.checkBtn,
        isActive && styles.checkBtnActive,
        isOnly && styles.checkBtnOnly,
      )}
      onClick={onClick}
    >
      {isOnly ? null : (
        <Icon className={styles.checkBtnIcon} />
      )}
      <span className={styles.checkBtnTitle}>
        {title}
      </span>
      <span>{value}</span>
    </div>
  )
}

function SelectView({
  handleNext,
  accounts: {
    phone, email, username,
  },
  onClose,
}: SelectViewProps) {
  const navigate = useNavigate()

  const [mode, setMode] = useState<Modes>(Modes.Email)
  const [isSent, setIsSent] = useState<boolean>(false)
  const [isVerification, setIsVerification] = useState<boolean>(false)

  const {
    mutate: phoneLoginMutation,
    data: phoneLoginData,
    error: phoneLoginError,
    isSuccess: isPhoneLoginSuccess,
    isError: isPhoneLoginError,
    reset: resetPhoneLogin,
  } = useLogin({ phone: phone || '' })

  useEffect(() => {
    if (isPhoneLoginSuccess) {
      setIsVerification(true)
    }
  }, [isPhoneLoginSuccess, phoneLoginData])

  useEffect(() => {
    if (isPhoneLoginError && phoneLoginError) {
      setIsVerification(false)
      resetPhoneLogin();
    }
  }, [isPhoneLoginError, phoneLoginError]);

  useEffect(() => {
    if (!email && phone) {
      setMode(Modes.Phone)
    }
  }, [phone, email])

  const {
    mutate: resetPasswordMutation,
    error: resetPasswordError,
    isSuccess: isResetPasswordSuccess,
    isError: isResetPasswordError,
    reset: resetResetPassword,
  }: any = useResetPassword({
    email: email || '',
    options: {
      redirectTo: `${window.location.origin}/new-pass`,
    },
  })

  if (isResetPasswordSuccess) {
    handleNext()
  }

  useEffect(() => {
    if (isResetPasswordError && resetPasswordError) {
      resetResetPassword();
    }
  }, [isResetPasswordError, resetPasswordError]);

  if (isResetPasswordError) {
    return (
      <MessageModal
        title="Error while reset pasword"
        status="error"
        onClick={onClose}
        message={resetPasswordError ? resetPasswordError?.message : 'Error occured while resetting the password. Please try again.'}
      />
    )
  }

  const image = false // TODO: replace with actual image later

  if ((mode === Modes.Phone) && isVerification) {
    return (
      <AuthModal
        onClose={onClose}
        title="Verification code"
        showLogo
      >
        <ConfirmView
          handleNext={() => navigate('new-pass')}
          resendToken={() => phoneLoginMutation()}
          phone={phone || ''}
          white
        />
      </AuthModal>
    )
  }

  if ((mode === Modes.Email) && isResetPasswordSuccess) {
    return (
      <MessageModal
        title="Please check your email"
        status="email"
        onClick={onClose}
        message="We have sent you a link to confirm your email address"
      />
    )
  }

  return (
    <AuthModal
      className={styles.container}
      onClose={onClose}
      title=""
      showLogo
    >
      <div
        className={clsx(
          styles.avatarContainer,
          (username && phone && email) && styles.avatarContainerTop,
        )}
      >
        {image ? (
          <img src={image} alt={username || ''} />
        ) : (
          <IconUser className={styles.avatarDefault} />
        )}
      </div>

      <div className={styles.username}>{username}</div>

      <div className={styles.usernameDescription}>
        Choose how you want to reset your password.
      </div>

      <div className={styles.footer}>
        <CheckBtn
          isOnly={!phone}
          isActive={mode === Modes.Email}
          onClick={() => setMode(Modes.Email)}
          title="Email will be sent to"
          value={email ? getMaskedEmail(email) : ''}
        />
        <CheckBtn
          isOnly={!email}
          isActive={mode === Modes.Phone}
          onClick={() => setMode(Modes.Phone)}
          title="Verification code will be sent to"
          value={phone ? getMasketPhone(phone) : ''}
        />

        <Button
          className={styles.btn}
          disabled={Boolean(isSent)}
          onClick={() => {
            setIsSent(true)
            if (mode === Modes.Email) {
              resetPasswordMutation()
            } else {
              phoneLoginMutation()
            }
          }}
          wide
          black
        >
          {(mode === Modes.Email) ? 'Send Link' : 'Send Code'}
        </Button>
      </div>
    </AuthModal>
  )
}

export default SelectView
